import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react"
import {
  CalendarEvent,
  fetchCalendarEvents,
} from "../helpers/google/calendar/fetchCalendarEvents"
import { useSession } from "next-auth/react"
import { deserialize } from "../helpers/database/setup/utils"
import { updateCalendarEventRSVPStatus } from "../helpers/google/calendar/updateCalendarEventRSVPStatus"

interface GlobalContextProps {
  calendarEvents: CalendarEvent[]
  updateCalendarEvents: () => Promise<void>
  loadingCalendarEvents: boolean
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
  children: ReactNode
}

export const CalendarContext: React.FC<GlobalProviderProps> = ({
  children,
}) => {
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])
  const [loadingCalendarEvents, setLoadingCalendarEvents] = useState(false)
  const { data: session, status } = useSession()
  const hasFetchedRef = useRef(false)
  const fetchTimeoutRef = useRef<number | null>(null)
  const idleCallbackRef = useRef<number | null>(null)
  const updateFnRef = useRef<(() => Promise<void>) | null>(null)

  const updateCalendarEvents = useCallback(async () => {
    if (!session?.user) {
      console.log("CalendarContext: No session, skipping calendar fetch")
      return
    }

    setLoadingCalendarEvents(true)

    try {
      const calendarEventData = await fetchCalendarEvents()
      if (calendarEventData) {
        const parsedCalendarEvents =
          deserialize<CalendarEvent[]>(calendarEventData)
        await setCalendarEvents(parsedCalendarEvents)
      }
    } catch (error) {
      console.error("Failed to fetch calendar events:", error)
    } finally {
      setLoadingCalendarEvents(false)
    }
  }, [session?.user])

  // Store the function in a ref so we can use it in useEffect without it being a dependency
  updateFnRef.current = updateCalendarEvents

  useEffect(() => {
    // Only run when session is available
    if (status === "loading") return
    if (status === "unauthenticated") return

    // Prevent multiple fetches - only fetch once per session
    if (session?.user && !hasFetchedRef.current) {
      hasFetchedRef.current = true

      // Defer calendar fetch until after initial render to improve page load performance
      // Use requestIdleCallback if available, otherwise fallback to setTimeout
      if (typeof window !== "undefined") {
        if ("requestIdleCallback" in window && window.requestIdleCallback) {
          idleCallbackRef.current = window.requestIdleCallback(
            () => {
              // Use ref to avoid stale closure
              if (updateFnRef.current) {
                updateFnRef.current()
              }
            },
            { timeout: 2000 },
          ) // Fallback after 2 seconds if browser is busy
        } else {
          // Fallback for browsers without requestIdleCallback
          // In browser, setTimeout returns a number
          const timeoutId = setTimeout(() => {
            // Use ref to avoid stale closure
            if (updateFnRef.current) {
              updateFnRef.current()
            }
          }, 100) // Small delay to allow initial render
          fetchTimeoutRef.current = Number(timeoutId)
        }
      }
    }

    // Cleanup function
    return () => {
      if (fetchTimeoutRef.current !== null) {
        clearTimeout(fetchTimeoutRef.current)
        fetchTimeoutRef.current = null
      }
      if (idleCallbackRef.current !== null) {
        cancelIdleCallback(idleCallbackRef.current)
        idleCallbackRef.current = null
      }
    }

    // Reset hasFetchedRef when session changes
    return () => {
      if (!session?.user) {
        hasFetchedRef.current = false
      }
    }
  }, [session?.user?.email, status])

  // Accept calendar events if calendar is free for the entire meeting time
  const acceptCalendarEventsIfFree = useCallback(async () => {
    if (calendarEvents.length > 0) {
      const calendarEventsNeedingAction = calendarEvents.filter(
        (event) => event.myResponseStatus === "needsAction",
      )
      for (const event of calendarEventsNeedingAction) {
        const eventStart = new Date(event.start)
        const eventEnd = new Date(event.end)
        // Check if calendar events overlaps with any other (not declined) event
        const overlapsWithOtherEvent = calendarEvents
          .filter((otherEvent) => otherEvent.myResponseStatus !== "declined")
          .some((otherEvent) => {
            const otherEventStart = new Date(otherEvent.start)
            const otherEventEnd = new Date(otherEvent.end)
            const overlaps =
              eventStart < otherEventEnd &&
              eventEnd > otherEventStart &&
              event.eventId !== otherEvent.eventId
            return overlaps
          })
        if (!overlapsWithOtherEvent) {
          await updateCalendarEventRSVPStatus(event, "accepted")
          updateCalendarEvents()
        }
      }
    }
  }, [calendarEvents, updateCalendarEvents])

  useEffect(() => {
    acceptCalendarEventsIfFree()
  }, [acceptCalendarEventsIfFree])

  return (
    <GlobalContext.Provider
      value={{
        calendarEvents,
        updateCalendarEvents,
        loadingCalendarEvents,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

// Custom hook for accessing the global context
export const useCalendarContext = () => useContext(GlobalContext)
